/**
 * PDF Node Renderer
 *
 * Renders PDF nodes showing one page at a time with navigation arrows.
 * The actual PDF-to-image rendering happens in the app layer (pdfjs-dist);
 * this renderer just displays the pre-rendered page image with nav controls.
 *
 * Node data:
 * - pageImageUrl: blob/data URL of the currently rendered page (set by app)
 * - currentPage: 1-based page number
 * - totalPages: total page count
 * - title: optional filename/title
 * - src: original PDF media URL (for AI visibility)
 */

import type { UINode, ScreenMetrics, WorldRect } from "../types";
import type {
  NodeRenderer,
  RenderLayers,
  RenderResult,
  LODLevel,
} from "../renderer";
import { calculateLOD } from "../renderer";
import { hashContent } from "../texture-cache";

/** PDF node data interface */
export interface PdfNodeData {
  /** Blob/data URL of the currently rendered page */
  pageImageUrl: string;
  /** 1-based current page number */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Optional title (typically filename) */
  title?: string;
  /** Original PDF media URL (for AI/retrieval — not used for rendering) */
  src?: string;
}

/**
 * PDF renderer with page navigation arrows and page counter.
 */
export class PdfRenderer implements NodeRenderer {
  private lastLOD: Map<string, LODLevel> = new Map();
  private lastContent: Map<string, string> = new Map();
  private contentHashes: Map<string, string> = new Map();

  render(
    node: UINode,
    metrics: ScreenMetrics,
    layers: RenderLayers,
  ): RenderResult {
    const opts = layers.options;

    const lod: LODLevel = opts.forceLod
      ? opts.forceLod
      : opts.simplifyLOD
        ? calculateLOD(metrics, {
            enableLodFull: opts.enableLodFull,
            enableLodTitle: opts.enableLodTitle,
            enableLodIcon: opts.enableLodIcon,
            enableLodDot: opts.enableLodDot,
          })
        : "full";

    const data = this.getData(node);
    const contentHash = this.getContentHash(node, lod);
    const lastHash = this.contentHashes.get(node.id);
    const lastLod = this.lastLOD.get(node.id);
    const lodChanged = lastLod !== lod;
    const isNewElement = layers.html.isNewlyCreated(node.id);

    layers.html.setDataAttribute(node.id, "lod", lod);

    // Only re-render if something changed
    if (!isNewElement && !lodChanged && contentHash === lastHash) {
      return { lod, changed: false };
    }

    const content = this.renderContent(data, lod);
    layers.html.setContent(node.id, content);

    this.lastLOD.set(node.id, lod);
    this.contentHashes.set(node.id, contentHash);
    this.lastContent.set(node.id, content);

    return { lod, changed: true };
  }

  private getData(node: UINode): PdfNodeData {
    const d = node.data as Record<string, unknown>;
    return {
      pageImageUrl: (d.pageImageUrl as string) || "",
      currentPage: (d.currentPage as number) || 1,
      totalPages: (d.totalPages as number) || 1,
      title: (d.title as string) || undefined,
      src: (d.src as string) || undefined,
    };
  }

  private getContentHash(node: UINode, lod: LODLevel): string {
    const d = this.getData(node);
    return hashContent(
      `pdf:${lod}:${d.pageImageUrl}:${d.currentPage}:${d.totalPages}:${d.title || ""}`,
    );
  }

  private renderContent(data: PdfNodeData, lod: LODLevel): string {
    switch (lod) {
      case "full":
        return this.renderFull(data);
      case "title":
        return this.renderTitle(data);
      case "icon":
        return this.renderIcon(data);
      case "dot":
        return `<div class="pdf-node pdf-dot"></div>`;
    }
  }

  private renderFull(data: PdfNodeData): string {
    const { pageImageUrl, currentPage, totalPages, title } = data;
    const hasPrev = currentPage > 1;
    const hasNext = currentPage < totalPages;

    const titleHtml = title
      ? `<div class="pdf-title">${this.escapeHtml(title)}</div>`
      : "";

    const imageHtml = pageImageUrl
      ? `<img
          src="${this.escapeHtml(pageImageUrl)}"
          alt="Page ${currentPage}"
          class="pdf-page-image"
          draggable="false"
        />`
      : `<div class="pdf-loading">Loading page...</div>`;

    return `
      <div class="node-close-btn" data-action="close">&times;</div>
      <div class="pdf-node pdf-full">
        ${imageHtml}
        ${titleHtml}
        <div class="pdf-nav">
          <button
            class="pdf-nav-btn pdf-nav-prev ${hasPrev ? "" : "disabled"}"
            data-action="pdf-prev"
            ${hasPrev ? "" : 'aria-disabled="true"'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          <span class="pdf-page-info">${currentPage} / ${totalPages}</span>
          <button
            class="pdf-nav-btn pdf-nav-next ${hasNext ? "" : "disabled"}"
            data-action="pdf-next"
            ${hasNext ? "" : 'aria-disabled="true"'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>
      </div>
    `;
  }

  private renderTitle(data: PdfNodeData): string {
    const { pageImageUrl, currentPage, totalPages, title } = data;
    const label = title || `PDF (${totalPages}p)`;

    const imageHtml = pageImageUrl
      ? `<img
          src="${this.escapeHtml(pageImageUrl)}"
          alt="${this.escapeHtml(label)}"
          class="pdf-page-image"
          draggable="false"
        />`
      : "";

    return `
      <div class="pdf-node pdf-title-lod">
        ${imageHtml}
        <div class="pdf-title">${this.escapeHtml(label)}</div>
      </div>
    `;
  }

  private renderIcon(data: PdfNodeData): string {
    const { pageImageUrl, title } = data;
    const label = title || "PDF";

    return `
      <div class="pdf-node pdf-icon">
        ${pageImageUrl ? `<img src="${this.escapeHtml(pageImageUrl)}" alt="${this.escapeHtml(label)}" class="pdf-page-image" draggable="false" />` : ""}
      </div>
    `;
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  getPreferredSize(node: UINode): { width: number; height: number } {
    return {
      width: node.size?.width ?? 300,
      height: node.size?.height ?? 400,
    };
  }

  cleanup(nodeId: string): void {
    this.lastLOD.delete(nodeId);
    this.lastContent.delete(nodeId);
    this.contentHashes.delete(nodeId);
  }
}
